import {
    UseQueryOptions,
    UseInfiniteQueryOptions,
    useQuery,
    useInfiniteQuery,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
    TRequestError,
    TConversationListProps,
    TConversationListResponse,
} from 'services/ChatMicroService'
import { QueryCacheKeys } from 'enums'
import { ChatMicroService } from 'services'
import { getRequestError } from 'utils/helpers'
import { useAppSelector } from 'store'
import { useAppQueriesData } from 'queryClient'

export type TFetchFnParams = TConversationListProps
export type TFetchFnResponse = TConversationListResponse
export type TQueryFnOpts = UseQueryOptions<
    TFetchFnResponse,
    [string, TRequestError<TFetchFnParams>],
    TFetchFnResponse,
    [string, TFetchFnParams]
>

export type TInfiniteQueryFnOpts = UseInfiniteQueryOptions<
    TFetchFnResponse,
    [string, TRequestError<TFetchFnParams>],
    TFetchFnResponse,
    TFetchFnResponse,
    [string, TFetchFnParams]
>

const key = QueryCacheKeys.conversationList

/**
 * Hook API messenger conversation list
 */
export default function useFetchConversationList(params: TFetchFnParams, opts: TQueryFnOpts = {}) {
    const { t } = useTranslation()
    const user = useAppSelector((state) => state.user)

    return useQuery([`${key}-${user.id}`, params], () => {
        return ChatMicroService.fetchConversationList(params)
            .then(({ data }) => {
                return data
            })
            .catch((err) => {
                return Promise.reject([getRequestError(err) || t('update_error'), err])
            })
    }, opts)
}

export function useFetchInfiniteConversationList(initParams: TFetchFnParams, opts: TInfiniteQueryFnOpts = {}) {
    const { t } = useTranslation()
    const user = useAppSelector((state) => state.user)

    return useInfiniteQuery([`${key}-${user.id}`, initParams], ({ pageParam = initParams }) => {
        return ChatMicroService.fetchConversationList(pageParam)
            .then(({ data }) => {
                return data
            })
            .catch((err) => {
                return Promise.reject([getRequestError(err) || t('update_error'), err])
            })
    }, {
        getNextPageParam: (lastPage, pages) => {
            return { ...initParams, page: pages.length + 1 }
        },
        ...opts,
    })
}

export function useSetQueriesDataConversationList() {
    const user = useAppSelector((state) => state.user)

    const {
        setQueriesData,
        invalidateQueries,
    } = useAppQueriesData<TFetchFnParams, TFetchFnResponse>(`${key}-${user.id}`)

    return {
        setQueriesData,
        invalidateQueries,
    }
}
